import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

export type DocumentStatus = 'DRAFT' | 'PUBLISHED' | 'LIVE';

export interface DocumentTreeItem {
  id: string;
  title: string;
  isFolder: boolean;
  sortOrder: number;
  status: DocumentStatus;
  createdBy: { id: string; name: string };
  updatedAt: string;
  children: DocumentTreeItem[];
}

export interface DocumentDetail {
  id: string;
  spaceId: string;
  title: string;
  content: any;
  contentText: string | null;
  parentDocumentId: string | null;
  status: DocumentStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  createdBy: string;
  updatedBy: string | null;
  creator: { id: string; name: string; avatarUrl: string | null };
  updater: { id: string; name: string; avatarUrl: string | null } | null;
  publisher: { id: string; name: string; avatarUrl: string | null } | null;
  createdAt: string;
  updatedAt: string;
  isFavorite: boolean;
  breadcrumb: { id: string; title: string }[];
  childDocuments: { id: string; title: string; sortOrder: number; status: DocumentStatus; updatedAt: string }[];
  /** DB에 Yjs 상태가 저장돼 있으면 true — 클라이언트 시드 결정에 사용 */
  hasYjsContent: boolean;
}

export interface DocumentFavoriteItem {
  id: string;
  createdAt: string;
  document: { id: string; title: string; updatedAt: string };
}

export async function fetchDocumentTree(spaceId: string) {
  const res = await api.get<ApiResponse<DocumentTreeItem[]>>(`/team-spaces/${spaceId}/documents`);
  return res.data.data!;
}

export async function fetchDocument(spaceId: string, documentId: string) {
  const res = await api.get<ApiResponse<DocumentDetail>>(`/team-spaces/${spaceId}/documents/${documentId}`);
  return res.data.data!;
}

export async function createDocument(
  spaceId: string,
  data: { title: string; content?: any; parentDocumentId?: string; isFolder?: boolean; sortOrder?: number },
) {
  const res = await api.post<ApiResponse<DocumentDetail>>(`/team-spaces/${spaceId}/documents`, data);
  return res.data.data!;
}

export async function updateDocument(
  spaceId: string,
  documentId: string,
  data: { title?: string; content?: any; parentDocumentId?: string | null; sortOrder?: number },
) {
  const res = await api.put<ApiResponse<DocumentDetail>>(`/team-spaces/${spaceId}/documents/${documentId}`, data);
  return res.data.data!;
}

export async function deleteDocument(spaceId: string, documentId: string) {
  await api.delete(`/team-spaces/${spaceId}/documents/${documentId}`);
}

export async function toggleDocumentFavorite(spaceId: string, documentId: string) {
  const res = await api.post<ApiResponse<{ isFavorite: boolean }>>(
    `/team-spaces/${spaceId}/documents/${documentId}/favorite`,
  );
  return res.data.data!;
}

export async function fetchDocumentFavorites(spaceId: string) {
  const res = await api.get<ApiResponse<DocumentFavoriteItem[]>>(`/team-spaces/${spaceId}/documents/favorites`);
  return res.data.data!;
}

export async function duplicateDocument(spaceId: string, documentId: string) {
  const res = await api.post<ApiResponse<DocumentDetail>>(`/team-spaces/${spaceId}/documents/${documentId}/duplicate`);
  return res.data.data!;
}

export async function updateDocumentStatus(spaceId: string, documentId: string, status: DocumentStatus) {
  const res = await api.put<ApiResponse<DocumentDetail>>(
    `/team-spaces/${spaceId}/documents/${documentId}/status`,
    { status },
  );
  return res.data.data!;
}

export async function uploadDocumentImage(spaceId: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<ApiResponse<{ url: string; fileName: string; fileSize: number; mimeType: string }>>(
    `/team-spaces/${spaceId}/documents/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data.data!;
}
